/**
 * TDD REFACTOR Phase - Production Sprite Animation Player Implementation
 * Author: Claude Code
 * Version: v3.0.1
 * Modified: 2025-01-22
 * 
 * Production-ready sprite animation player with frame-by-frame playback,
 * layered rendering, priority system, and comprehensive animation controls.
 */

import * as Phaser from 'phaser';
import { SpriteAssetLoader } from './SpriteAssetLoader';
import { 
  CharacterType, 
  WeaponType, 
  AnimationType, 
  getFrameCount,
  getAnimationPriority,
  ANIMATION_FRAME_CONFIG
} from './spriteTypes';
import { getCharacterFrameKey, getWeaponFrameKey } from './utils/spriteKeyUtils';
import AnimationDebugLogger from './AnimationDebugLogger';

// Debug flags (set to true only during targeted troubleshooting)
const DEBUG_ANIM = false;

// Animation playback options interface
interface AnimationOptions {
  loop: boolean;
  fps: number;
  onComplete?: () => void;
  flipX?: boolean;
  parentContainer?: Phaser.GameObjects.Container;
  // Optional scale to keep layered sprites visually consistent with base sprites
  scale?: number;
}

// Position interface
interface Position {
  x: number;
  y: number;
}

// Animation state interface for runtime tracking
interface PlaybackAnimationState {
  agentId: string;
  characterType: CharacterType;
  weaponType?: WeaponType;
  animationType: AnimationType;
  currentFrame: number;
  totalFrames: number;
  isPlaying: boolean;
  isPaused: boolean;
  isLooping: boolean;
  fps: number;
  priority: number;
  sprites: {
    character: Phaser.GameObjects.Sprite;
    weapon?: Phaser.GameObjects.Sprite;
  };
  timer?: Phaser.Time.TimerEvent;
  onComplete?: () => void;
}

// Layered sprites interface
interface LayeredSprites {
  character: Phaser.GameObjects.Sprite;
  weapon?: Phaser.GameObjects.Sprite;
}

// Performance statistics interface
interface PerformanceStats {
  activeAnimations: number;
  totalSprites: number;
  memoryUsage: number;
  averageFPS: number;
}

export class SpriteAnimationPlayer {
  public readonly scene: Phaser.Scene;
  public readonly assetLoader: SpriteAssetLoader;
  public readonly activeAnimations: Map<string, PlaybackAnimationState> = new Map();
  private readonly errors: string[] = [];
  private readonly createdAnimations = new Set<string>();
  // Ensure layered sprites render above theater overlay/window
  private static readonly LAYERED_SPRITE_DEPTH = 1004;
  
  // Constants for better maintainability
  private static readonly DEFAULT_FPS = 10;
  private static readonly MEMORY_ESTIMATE_PER_SPRITE = 1024; // bytes
  private static readonly MAX_ERRORS = 100; // Prevent memory leaks from error accumulation

  constructor(scene: Phaser.Scene, assetLoader: SpriteAssetLoader) {
    this.scene = scene;
    this.assetLoader = assetLoader;
  }

  // Create Phaser animation definition for character (inspired by pokemonAutoChess)
  createCharacterAnimation(characterType: CharacterType, animationType: AnimationType): void {
    const animKey = `char-${characterType}-${animationType}`;
    
    if (this.createdAnimations.has(animKey) || this.scene.anims.exists(animKey)) {
      return; // Already created
    }

    const frameCount = getFrameCount(animationType);
    const config = ANIMATION_FRAME_CONFIG[animationType];
    
    // Generate frame names that match our texture keys
    const frames = [];
    for (let i = 0; i < frameCount; i++) {
      const frameStr = config.useDoubleDigit ? 
        i.toString().padStart(2, '0') : 
        i.toString();
      const textureKey = `char-${characterType}-${animationType}-${frameStr}`;
      frames.push({ key: textureKey, frame: null });
    }

    // Determine if animation should loop
    const shouldLoop = [AnimationType.IDLE, AnimationType.MOVE].includes(animationType);
    
    if (DEBUG_ANIM) console.log(`Creating Phaser animation: ${animKey} with ${frames.length} frames`);

    this.scene.anims.create({
      key: animKey,
      frames: frames,
      frameRate: SpriteAnimationPlayer.DEFAULT_FPS,
      repeat: shouldLoop ? -1 : 0
    });

    this.createdAnimations.add(animKey);
  }

  // Display a specific frame of character animation
  displayFrame(
    agentId: string,
    characterType: CharacterType,
    animationType: AnimationType,
    frame: number,
    x: number,
    y: number
  ): Phaser.GameObjects.Sprite | null {
    try {
      const textureKey = getCharacterFrameKey(characterType, animationType, frame);

      // Check if texture key indicates missing texture
      if (textureKey === 'missing-texture') {
        this.addError(textureKey);
        return null;
      }

      const sprite = this.scene.add.sprite(x, y, textureKey);
      
      if (!sprite) {
        this.addError(textureKey);
        return null;
      }

      return sprite;
    } catch (error) {
      this.addError(`${characterType}-${animationType}-${frame}`);
      return null;
    }
  }

  // Display layered character + weapon frame
  displayLayeredFrame(
    agentId: string,
    characterType: CharacterType,
    weaponType: WeaponType,
    animationType: AnimationType,
    frame: number,
    x: number,
    y: number
  ): LayeredSprites {
    const characterKey = getCharacterFrameKey(characterType, animationType, frame);
    const weaponKey = getWeaponFrameKey(weaponType, animationType, frame);

    const characterSprite = this.scene.add.sprite(x, y, characterKey);
    const weaponSprite = this.scene.add.sprite(x, y, weaponKey);

    // Bring layered sprites above theater overlay and base agents
    characterSprite.setDepth(SpriteAnimationPlayer.LAYERED_SPRITE_DEPTH);
    weaponSprite.setDepth(SpriteAnimationPlayer.LAYERED_SPRITE_DEPTH);

    return {
      character: characterSprite,
      weapon: weaponSprite
    };
  }

  // Start playing character animation sequence
  async playAnimation(
    agentId: string,
    characterType: CharacterType,
    animationType: AnimationType,
    position: Position,
    options: AnimationOptions
  ): Promise<boolean> {
    if (DEBUG_ANIM) console.log(`playAnimation called with:`, { agentId, characterType, animationType, position, options });
    
    try {
      // Determine first frame key and prefer Phaser texture cache as来源
      const firstFrameKey = getCharacterFrameKey(characterType, animationType, 0);
      // Check if animation is loaded (either marked or textures exist)
      const isLoaded = this.assetLoader.isAnimationLoaded(characterType, animationType);
      const textureExistsEarly = this.scene.textures.exists(firstFrameKey);
      if (DEBUG_ANIM) console.log(`Animation loaded check: ${characterType}-${animationType} = ${isLoaded}, textureExists=${textureExistsEarly}`);
      if (!isLoaded && !textureExistsEarly) {
        this.addError(`Animation not loaded: ${characterType}-${animationType}`);
        console.error(`Animation not loaded: ${characterType}-${animationType}`);
        return false;
      }

      // Check priority if animation is already playing
      const existingState = this.activeAnimations.get(agentId);
      const newPriority = getAnimationPriority(animationType);
      
      if (existingState && existingState.priority >= newPriority) {
        return false; // Lower or equal priority, don't play
      }

      // Stop existing animation if any
      if (existingState) {
        this.stopAnimation(agentId);
      }

      // Create Phaser animation definition
      this.createCharacterAnimation(characterType, animationType);
      
      // Get first frame texture key for sprite creation (already computed)
      if (DEBUG_ANIM) console.log(`Creating sprite with texture key: ${firstFrameKey}`);
      AnimationDebugLogger.log('event', 'PLAY_START', {
        id: agentId,
        characterType,
        animationType,
        firstFrameKey
      }, { sceneTime: (this.scene.time as any)?.now, frame: (this.scene.game as any)?.loop?.frame });
      
      // Check if texture exists
      const textureExists = this.scene.textures.exists(firstFrameKey);
      console.log(`Texture exists in Phaser cache: ${textureExists}`);
      
      if (!textureExists) {
        console.error(`Texture not found in Phaser cache: ${firstFrameKey}`);
        console.log(`Available textures:`, Object.keys(this.scene.textures.list));
        return false;
      }
      
      // Create sprite with first frame
      const sprite = this.scene.add.sprite(position.x, position.y, firstFrameKey);
      if (!sprite) {
        console.error(`Failed to create sprite with texture: ${firstFrameKey}`);
        return false;
      }
      
      if (DEBUG_ANIM) console.log(`Sprite created successfully at position (${position.x}, ${position.y})`);
      sprite.setVisible(true);
      // If a specific scale is provided, respect it. Otherwise default to 2 for visibility in demos.
      if (typeof options.scale === 'number') {
        sprite.setScale(options.scale);
      } else {
        sprite.setScale(2);
      }

      // Optional flip
      if (options.flipX) {
        sprite.setFlipX(true);
      }

      // Optionally attach to a parent container (e.g., theater window)
      if (options.parentContainer) {
        options.parentContainer.add(sprite);
        const anyContainer = options.parentContainer as any;
        if (anyContainer.bringToTop) {
          anyContainer.bringToTop(sprite);
        }
      }
      
      // Play the Phaser animation
      const animKey = `char-${characterType}-${animationType}`;
      if (DEBUG_ANIM) console.log(`Starting Phaser animation: ${animKey}`);
      sprite.anims.play(animKey);

      // Create animation state
      const animationState: PlaybackAnimationState = {
        agentId,
        characterType,
        animationType,
        currentFrame: 0,
        totalFrames: getFrameCount(animationType),
        isPlaying: true,
        isPaused: false,
        isLooping: options.loop,
        fps: options.fps,
        priority: newPriority,
        sprites: { character: sprite },
        onComplete: options.onComplete
      };

      // Setup frame timer
      const frameInterval = 1000 / options.fps;
      animationState.timer = this.scene.time.addEvent({
        delay: frameInterval,
        callback: () => this.advanceFrame(agentId),
        loop: true
      });

      this.activeAnimations.set(agentId, animationState);
      return true;
    } catch (error) {
      return false;
    }
  }

  // Start playing layered character + weapon animation
  async playLayeredAnimation(
    agentId: string,
    characterType: CharacterType,
    weaponType: WeaponType,
    animationType: AnimationType,
    position: Position,
    options: AnimationOptions
  ): Promise<boolean> {
    try {
      // Stop existing animation
      const existingState = this.activeAnimations.get(agentId);
      if (existingState) {
        this.stopAnimation(agentId);
      }

      // Get first frame texture keys
      const characterTextureKey = this.assetLoader.getCharacterTextureKey(characterType, animationType, 0);
      const weaponTextureKey = this.assetLoader.getWeaponTextureKey(weaponType, animationType, 0);
      if (DEBUG_ANIM) console.log(`Creating layered sprites:`, { characterTextureKey, weaponTextureKey });
      AnimationDebugLogger.log('event', 'PLAY_START_LAYERED', {
        id: agentId,
        characterType,
        weaponType,
        animationType,
        characterTextureKey,
        weaponTextureKey
      }, { sceneTime: (this.scene.time as any)?.now, frame: (this.scene.game as any)?.loop?.frame });
      
      // Ensure textures exist in Phaser cache (source of truth)
      const charExists = this.scene.textures.exists(characterTextureKey);
      const weapExists = this.scene.textures.exists(weaponTextureKey);
      if (!charExists || !weapExists) {
        console.warn('Layered textures missing in cache', { characterTextureKey, weaponTextureKey, charExists, weapExists });
        return false;
      }
      
      // Create sprites
      const characterSprite = this.scene.add.sprite(position.x, position.y, characterTextureKey);
      const weaponSprite = this.scene.add.sprite(position.x, position.y, weaponTextureKey);

      // 可选：附着到父容器（确保与Agent容器同移动/同深度）
      if (options.parentContainer) {
        options.parentContainer.add(characterSprite);
        options.parentContainer.add(weaponSprite);
        const anyContainer = options.parentContainer as any;
        if (anyContainer.bringToTop) {
          anyContainer.bringToTop(characterSprite);
          anyContainer.bringToTop(weaponSprite);
        }
      }

      // 缩放（与Agent尺寸一致）
      if (typeof options.scale === 'number') {
        characterSprite.setScale(options.scale);
        weaponSprite.setScale(options.scale);
      }

      // Ensure visibility above overlay/window
      characterSprite.setDepth(SpriteAnimationPlayer.LAYERED_SPRITE_DEPTH);
      weaponSprite.setDepth(SpriteAnimationPlayer.LAYERED_SPRITE_DEPTH + 1); // weapon on top
      
      // Apply scale if provided (match theater base agent scale)
      if (typeof options.scale === 'number') {
        characterSprite.setScale(options.scale);
        weaponSprite.setScale(options.scale);
      }

      // Optional flip for left-side attacks
      if (options.flipX) {
        characterSprite.setFlipX(true);
        weaponSprite.setFlipX(true);
      }

      // Optionally attach to a parent container (theater window)
      if (options.parentContainer) {
        options.parentContainer.add([characterSprite, weaponSprite]);
        // Bring to top within container to ensure above base sprites/HP bars
        const anyContainer = options.parentContainer as any;
        if (anyContainer.bringToTop) {
          anyContainer.bringToTop(characterSprite);
          anyContainer.bringToTop(weaponSprite);
        }
      }
      
      if (!characterSprite || !weaponSprite) {
        console.error(`Failed to create layered sprites`);
        return false;
      }
      
      if (DEBUG_ANIM) console.log(`Layered sprites created successfully at position (${position.x}, ${position.y})`);
      characterSprite.setVisible(true);
      weaponSprite.setVisible(true);

      // Create layered animation state
      const animationState: PlaybackAnimationState = {
        agentId,
        characterType,
        weaponType,
        animationType,
        currentFrame: 0,
        totalFrames: getFrameCount(animationType),
        isPlaying: true,
        isPaused: false,
        isLooping: options.loop,
        fps: options.fps,
        priority: getAnimationPriority(animationType),
        sprites: { character: characterSprite, weapon: weaponSprite },
        onComplete: options.onComplete
      };

      // Setup frame timer
      const frameInterval = 1000 / options.fps;
      animationState.timer = this.scene.time.addEvent({
        delay: frameInterval,
        callback: () => this.advanceFrame(agentId),
        loop: true
      });

      this.activeAnimations.set(agentId, animationState);
      return true;
    } catch (error) {
      return false;
    }
  }

  // Advance animation to next frame
  advanceFrame(agentId: string): void {
    const state = this.activeAnimations.get(agentId);
    if (!state || !state.isPlaying || state.isPaused) {
      return;
    }

    // 若精灵已被销毁或脱离场景，立即停止该动画，避免调用 Phaser API 抛错
    try {
      const ch = state.sprites?.character as any;
      if (!ch || ch.destroyed || !ch.scene || !ch.scene.sys) {
        this.stopAnimation(agentId);
        return;
      }
      if (state.sprites.weapon) {
        const wp: any = state.sprites.weapon;
        if (wp && (wp.destroyed || !wp.scene || !wp.scene.sys)) {
          // 武器层被销毁也视为不可继续推进分层动画
          this.stopAnimation(agentId);
          return;
        }
      }
    } catch {
      // 安全回落：任何异常都停止该动画，防止主循环卡死
      this.stopAnimation(agentId);
      return;
    }

    // Update frame
    state.currentFrame++;

    // 🎯 死亡动画特殊处理：添加额外的空白帧
    const isDeathAnimation = state.animationType === AnimationType.DIE;
    const effectiveTotalFrames = isDeathAnimation ? state.totalFrames + 1 : state.totalFrames;

    // Check if animation completed
    if (state.currentFrame >= effectiveTotalFrames) {
      if (state.isLooping) {
        state.currentFrame = 0; // Loop back to start
      } else {
        // Animation completed
        state.isPlaying = false;
        if (state.timer) {
          state.timer.destroy();
        }
        if (state.onComplete) {
          state.onComplete();
        }
        return;
      }
    }

    // Update sprite textures (with special handling for death animation final frame)
    this.updateSpriteTextures(state);

    // Log per-frame after textures updated
    try {
      const charKey = getCharacterFrameKey(state.characterType, state.animationType, state.currentFrame);
      const wepKey = state.weaponType ? getWeaponFrameKey(state.weaponType, state.animationType, state.currentFrame) : undefined;
      AnimationDebugLogger.log('frame', 'ADV_FRAME', {
        id: agentId,
        animationType: state.animationType,
        currentFrame: state.currentFrame,
        totalFrames: state.totalFrames,
        charKey,
        wepKey
      }, { sceneTime: (this.scene.time as any)?.now, frame: (this.scene.game as any)?.loop?.frame });
    } catch {}
  }

  // Update sprite textures to current frame
  private updateSpriteTextures(state: PlaybackAnimationState): void {
    const isDeathAnimation = state.animationType === AnimationType.DIE;
    const isExtraDeathFrame = isDeathAnimation && state.currentFrame >= state.totalFrames;

    if (isExtraDeathFrame) {
      // 🎯 死亡动画额外帧：完全隐藏sprite实现消失效果
      console.log(`💀 死亡动画额外空白帧: ${state.agentId} frame=${state.currentFrame}/${state.totalFrames}`);
      state.sprites.character.setVisible(false);
      if (state.sprites.weapon) {
        state.sprites.weapon.setVisible(false);
      }
    } else {
      // 正常帧更新
      // 确保sprite可见（防止之前被隐藏）
      try { state.sprites.character.setVisible(true); } catch {}
      if (state.sprites.weapon) { try { state.sprites.weapon.setVisible(true); } catch {} }

      // Update character sprite
      const characterKey = getCharacterFrameKey(state.characterType, state.animationType, state.currentFrame);
      try { state.sprites.character.setTexture(characterKey); } catch { /* sprite 可能已销毁，外层会stop */ }

      // Update weapon sprite if present
      if (state.sprites.weapon && state.weaponType) {
        const weaponKey = getWeaponFrameKey(state.weaponType, state.animationType, state.currentFrame);
        try { state.sprites.weapon.setTexture(weaponKey); } catch {}
      }
    }
  }

  // Check if agent is playing animation
  isPlaying(agentId: string): boolean {
    const state = this.activeAnimations.get(agentId);
    return state ? state.isPlaying : false;
  }

  // Check if agent animation is paused
  isPaused(agentId: string): boolean {
    const state = this.activeAnimations.get(agentId);
    return state ? state.isPaused : false;
  }

  // Pause animation
  pauseAnimation(agentId: string): void {
    const state = this.activeAnimations.get(agentId);
    if (state && state.isPlaying) {
      state.isPaused = true;
      state.isPlaying = false;
      if (state.timer) {
        state.timer.paused = true;
      }
    }
  }

  // Resume paused animation
  resumeAnimation(agentId: string): void {
    const state = this.activeAnimations.get(agentId);
    if (state && state.isPaused) {
      state.isPaused = false;
      state.isPlaying = true;
      if (state.timer) {
        state.timer.paused = false;
      }
    }
  }

  // Stop animation completely
  stopAnimation(agentId: string): void {
    const state = this.activeAnimations.get(agentId);
    if (state) {
      state.isPlaying = false;
      if (state.timer) {
        state.timer.destroy();
      }
      if (state.sprites.character) {
        state.sprites.character.destroy();
      }
      if (state.sprites.weapon) {
        state.sprites.weapon.destroy();
      }
      this.activeAnimations.delete(agentId);
    }
  }

  // Get current animation type for agent
  getCurrentAnimation(agentId: string): AnimationType | undefined {
    const state = this.activeAnimations.get(agentId);
    return state ? state.animationType : undefined;
  }

  // Change animation while playing
  async changeAnimation(
    agentId: string,
    newAnimationType: AnimationType,
    options: AnimationOptions
  ): Promise<boolean> {
    const state = this.activeAnimations.get(agentId);
    if (!state) {
      return false;
    }

    const newPriority = getAnimationPriority(newAnimationType);
    if (state.priority >= newPriority) {
      return false; // Cannot change to lower or equal priority
    }

    // Play new animation
    return await this.playAnimation(
      agentId,
      state.characterType,
      newAnimationType,
      { x: state.sprites.character.x, y: state.sprites.character.y },
      options
    );
  }

  // Force animation change regardless of priority
  async forceChangeAnimation(
    agentId: string,
    newAnimationType: AnimationType,
    options: AnimationOptions
  ): Promise<boolean> {
    const state = this.activeAnimations.get(agentId);
    if (!state) {
      return false;
    }

    // Save position and character type before stopping
    const position = { x: state.sprites.character.x, y: state.sprites.character.y };
    const characterType = state.characterType;
    
    // Force stop existing animation first
    this.stopAnimation(agentId);
    
    // Then play new animation without priority check
    return await this.playAnimation(
      agentId,
      characterType,
      newAnimationType,
      position,
      options
    );
  }

  // Get animation state for agent
  getAnimationState(agentId: string): PlaybackAnimationState | undefined {
    return this.activeAnimations.get(agentId);
  }

  // Add error with limit to prevent memory leaks
  private addError(error: string): void {
    this.errors.push(error);
    if (this.errors.length > SpriteAnimationPlayer.MAX_ERRORS) {
      this.errors.shift(); // Remove oldest error
    }
  }

  // Get error messages
  getErrors(): string[] {
    return [...this.errors];
  }

  // Clear error log
  clearErrors(): void {
    this.errors.length = 0;
  }

  // Get performance statistics
  getPerformanceStats(): PerformanceStats {
    let totalSprites = 0;
    let totalFPS = 0;
    
    this.activeAnimations.forEach(state => {
      totalSprites += state.sprites.weapon ? 2 : 1;
      totalFPS += state.fps;
    });

    const averageFPS = this.activeAnimations.size > 0 
      ? totalFPS / this.activeAnimations.size 
      : SpriteAnimationPlayer.DEFAULT_FPS;

    return {
      activeAnimations: this.activeAnimations.size,
      totalSprites,
      memoryUsage: totalSprites * SpriteAnimationPlayer.MEMORY_ESTIMATE_PER_SPRITE,
      averageFPS: Math.round(averageFPS * 100) / 100 // Round to 2 decimal places
    };
  }

  // Cleanup all animations
  destroy(): void {
    this.activeAnimations.forEach((state, agentId) => {
      this.stopAnimation(agentId);
    });
    this.activeAnimations.clear();
    this.errors.length = 0;
  }

  // Retarget an existing layered animation to a new animationType without recreating sprites
  // Keeps weapon sprite to avoid one-frame delay and flicker
  retargetLayeredAnimation(
    agentId: string,
    newAnimationType: AnimationType,
    options: { loop: boolean; fps: number; flipX?: boolean; onComplete?: () => void }
  ): boolean {
    const state = this.activeAnimations.get(agentId);
    if (!state || !state.sprites.character) return false;

    // Ensure we have layered sprites (weapon may be optional)
    state.animationType = newAnimationType;
    state.currentFrame = 0;
    state.totalFrames = getFrameCount(newAnimationType);
    state.isLooping = options.loop;
    state.fps = options.fps;
    state.onComplete = options.onComplete;
    state.isPlaying = true;

    if (typeof options.flipX === 'boolean') {
      state.sprites.character.setFlipX(options.flipX);
      state.sprites.weapon?.setFlipX(options.flipX);
    }

    // Restart timer with new fps
    if (state.timer) {
      try { state.timer.destroy(); } catch {}
    }
    const frameInterval = 1000 / options.fps;
    state.timer = this.scene.time.addEvent({
      delay: frameInterval,
      callback: () => this.advanceFrame(agentId),
      loop: true
    });

    // Immediately set frame 0 textures to avoid first-frame lag
    this.updateSpriteTextures(state);
    return true;
  }
}
